/** Destination search: shared saved places, debounced geocoding, recents. */

import type { LngLat, Place } from '../core/types';
import { searchPlaces } from '../search/geocode';
import { listRecents } from '../core/db';
import { formatDistance } from '../core/format';
import { store } from '../core/store';
import type { SavedPlace } from '../auth/api';
import { distance } from '../core/geo';
import { t } from '../core/i18n';

export class SearchPanel {
  readonly el: HTMLElement;
  private input!: HTMLInputElement;
  private list!: HTMLElement;
  private debounce: number | null = null;
  private inflight: AbortController | null = null;

  constructor(
    parent: HTMLElement,
    private getOrigin: () => LngLat | null,
    private onPick: (place: Place) => void,
    private onClose: () => void,
    private getSaved: () => SavedPlace[],
    private onDeleteSaved: (id: string) => Promise<void>,
    /** Open the rename dialog for a saved place; resolves once renamed. */
    private onRenameSaved: (place: SavedPlace) => Promise<void>,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel panel--search';
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="panel__bar">
        <button class="icon-btn" data-act="close" aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="M15 4 L7 12 L15 20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <input class="panel__input" type="search" placeholder="${t('search.ph')}"
               autocomplete="off" autocorrect="off" spellcheck="false" enterkeyhint="search"/>
      </div>
      <div class="panel__list" role="listbox"></div>`;
    parent.appendChild(this.el);

    this.input = this.el.querySelector('.panel__input')!;
    this.list = this.el.querySelector('.panel__list')!;

    this.el.querySelector('[data-act=close]')!.addEventListener('click', () => this.onClose());
    this.input.addEventListener('input', () => this.queueSearch());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.runSearch();
      }
    });
  }

  open(): void {
    this.el.hidden = false;
    this.input.value = '';
    void this.showRecents();
    // Delay focus so the open transition isn't janked by the keyboard.
    setTimeout(() => this.input.focus(), 180);
  }

  close(): void {
    this.el.hidden = true;
    this.input.blur();
    this.inflight?.abort();
  }

  private queueSearch(): void {
    if (this.debounce !== null) clearTimeout(this.debounce);
    this.debounce = window.setTimeout(() => this.runSearch(), 320);
  }

  private async runSearch(): Promise<void> {
    const q = this.input.value.trim();
    if (q.length < 2) {
      void this.showRecents();
      return;
    }
    this.inflight?.abort();
    this.inflight = new AbortController();

    // Fuzzy-match the fleet's saved places and recents instantly - typing
    // "showrom" finds "Showroom" before any network round-trip. Recents are
    // searched in full (name AND address, the whole history), so a place
    // driven to months ago is findable without the geocoder.
    const saved = this.getSaved();
    const recents = await listRecents(500);
    const localSaved = saved
      .map((p) => ({ p, score: fuzzyScore(q, p.name) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
    const localRecent = recents
      .map((p) => ({
        p,
        score: Math.max(fuzzyScore(q, p.name), p.address ? fuzzyScore(q, p.address) : 0),
      }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    const localHtml =
      localSaved.map((x) => this.savedRow(x.p)).join('') + this.placesHtml(localRecent.map((x) => x.p), true);
    this.list.innerHTML = localHtml + `<div class="panel__hint">${t('search.searching')}</div>`;
    this.bindSaved(saved);
    this.bindPlaceRows(localRecent.map((x) => x.p));

    try {
      const places = await searchPlaces(q, this.getOrigin(), this.inflight.signal);
      // Avoid duplicating a remote hit that's already shown as a local match.
      const localIds = new Set(localRecent.map((x) => x.p.id));
      const remote = places.filter((p) => !localIds.has(p.id));
      this.list.innerHTML = localHtml + this.placesHtml(remote, false);
      this.bindSaved(saved);
      this.bindPlaceRows([...localRecent.map((x) => x.p), ...remote]);
      if (!remote.length && !localSaved.length && !localRecent.length) {
        this.list.innerHTML = `<div class="panel__hint">${t('search.noResults')}</div>`;
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const failHtml = `<div class="panel__hint">${store.get().online ? t('search.failed') : t('search.offline')}</div>`;
      this.list.innerHTML = localHtml + failHtml;
      this.bindSaved(saved);
      this.bindPlaceRows(localRecent.map((x) => x.p));
    }
  }

  private savedRow(p: SavedPlace): string {
    const origin = this.getOrigin();
    const units = store.get().settings.units;
    const iconClass = p.kind === 'shared-location' ? 'live' : (p.category ?? 'star');
    return `
      <div class="place place--saved" data-saved="${p.id}">
        <span class="place__icon place__icon--${iconClass}">${categoryIcon(p)}</span>
        <span class="place__text">
          <span class="place__name">${escapeHtml(p.name)}</span>
          <span class="place__addr">${t('search.by', { name: escapeHtml(p.creatorName) })}</span>
        </span>
        ${
          origin
            ? `<span class="place__dist">${formatDistance(distance(origin, [p.lng, p.lat]), units)}</span>`
            : ''
        }
        <button class="place__ren" data-rensaved="${p.id}" aria-label="Rename saved place">
          <svg viewBox="0 0 24 24"><path d="M4 20 L8.5 19 L19.5 8 a1.8 1.8 0 0 0 -2.5 -2.5 L6 16.5 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>
        </button>
        <button class="place__del" data-delsaved="${p.id}" aria-label="Delete saved place">&#215;</button>
      </div>`;
  }

  /** Google-style one-tap shortcuts: Home, Showroom, Depot… sorted by category. */
  private quickChipsHtml(saved: SavedPlace[]): string {
    const chips = saved
      .filter((p) => p.kind !== 'shared-location')
      .sort(
        (a, b) =>
          (CATEGORY_ORDER[a.category ?? 'other'] ?? 9) - (CATEGORY_ORDER[b.category ?? 'other'] ?? 9),
      )
      .slice(0, 8);
    if (!chips.length) return '';
    return `<div class="quick-chips">${chips
      .map(
        (p) => `
      <button class="quick-chip" data-quick="${p.id}">
        ${categoryIcon(p)}<span>${escapeHtml(p.name)}</span>
      </button>`,
      )
      .join('')}</div>`;
  }

  private async showRecents(): Promise<void> {
    const recents = await listRecents();
    const saved = this.getSaved();
    if (!recents.length && !saved.length) {
      this.list.innerHTML = `<div class="panel__hint">${t('search.empty')}</div>`;
      return;
    }

    const savedHtml = saved.length
      ? this.quickChipsHtml(saved) +
        `<div class="panel__section">${t('search.saved')}</div>` +
        saved.map((p) => this.savedRow(p)).join('')
      : '';

    this.list.innerHTML = savedHtml + (recents.length ? `<div class="panel__section">${t('search.recent')}</div>` : '');
    this.bindSaved(saved);
    if (recents.length) this.appendPlaces(recents, true);
  }

  private bindSaved(saved: SavedPlace[]): void {
    this.list.querySelectorAll<HTMLButtonElement>('[data-quick]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const p = saved.find((x) => x.id === chip.dataset.quick);
        if (p) {
          this.onPick({
            id: `saved:${p.id}`,
            name: p.name,
            address: t('place.savedBy', { name: p.creatorName }),
            center: [p.lng, p.lat],
            kind: 'saved',
          });
        }
      });
    });
    this.list.querySelectorAll<HTMLElement>('.place--saved').forEach((row) => {
      row.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.place__del, .place__ren')) return;
        const p = saved.find((x) => x.id === row.dataset.saved);
        if (p) {
          this.onPick({
            id: `saved:${p.id}`,
            name: p.name,
            address: t('place.savedBy', { name: p.creatorName }),
            center: [p.lng, p.lat],
            kind: 'saved',
          });
        }
      });
    });
    this.list.querySelectorAll<HTMLButtonElement>('[data-delsaved]').forEach((btn) => {
      btn.addEventListener('click', () => {
        void this.onDeleteSaved(btn.dataset.delsaved!)
          .then(() => this.showRecents())
          .catch((err) => alert(err instanceof Error ? err.message : t('search.deleteFailed')));
      });
    });
    this.list.querySelectorAll<HTMLButtonElement>('[data-rensaved]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const p = saved.find((x) => x.id === btn.dataset.rensaved);
        if (!p) return;
        void this.onRenameSaved(p)
          .then(() => this.showRecents())
          .catch(() => {});
      });
    });
  }

  private appendPlaces(places: Place[], recent: boolean): void {
    const frag = document.createElement('div');
    frag.innerHTML = this.placesHtml(places, recent);
    while (frag.firstChild) this.list.appendChild(frag.firstChild);
    this.bindPlaceRows(places);
  }

  private placesHtml(places: Place[], recent: boolean): string {
    const units = store.get().settings.units;
    // When nothing matched the street, say what to do about it rather than
    // leaving the driver to work out why the answers look wrong.
    const hint =
      places.length && places.every((p) => p.approximate)
        ? `<div class="place-hint">${t('search.approxHint')}</div>`
        : '';
    return hint + places
      .map(
        (p, i) => `
        <button class="place ${p.approximate ? 'is-approx' : ''}" role="option" data-i="${i}">
          <span class="place__icon">${recent ? CLOCK_SVG : PIN_SVG}</span>
          <span class="place__text">
            <span class="place__name">${escapeHtml(p.name)}</span>
            <span class="place__addr">${escapeHtml(p.address)}</span>
            ${
              p.approximate
                ? `<span class="place__warn">${t('search.approx')}</span>`
                : ''
            }
          </span>
          ${p.distance != null ? `<span class="place__dist">${formatDistance(p.distance, units)}</span>` : ''}
        </button>`,
      )
      .join('');
  }

  private bindPlaceRows(places: Place[]): void {
    // Bind by DOM order (matches the order rows were rendered), not data-i -
    // concatenated lists would otherwise collide on indices. `onclick` keeps
    // re-binding idempotent.
    this.list.querySelectorAll<HTMLButtonElement>('button.place').forEach((btn, i) => {
      const place = places[i];
      if (place) btn.onclick = () => this.onPick(place);
    });
  }
}

const PIN_SVG = `<svg viewBox="0 0 24 24"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" fill="currentColor"/></svg>`;
const STAR_SVG = `<svg viewBox="0 0 24 24"><path d="M12 3 L14.7 8.6 L20.9 9.4 L16.4 13.7 L17.5 19.8 L12 16.9 L6.5 19.8 L7.6 13.7 L3.1 9.4 L9.3 8.6 Z" fill="currentColor"/></svg>`;
const LIVE_SVG = `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" fill="currentColor"/><path d="M5 12 a7 7 0 0 1 14 0 M8 12 a4 4 0 0 1 8 0" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>`;
const HOME_SVG = `<svg viewBox="0 0 24 24"><path d="M4 11 L12 4 L20 11 M6 10 V19 a1 1 0 0 0 1 1 h3 v-5 h4 v5 h3 a1 1 0 0 0 1-1 V10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const WORK_SVG = `<svg viewBox="0 0 24 24"><rect x="4" y="8" width="16" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M9 8 V6 a1.5 1.5 0 0 1 1.5-1.5 h3 A1.5 1.5 0 0 1 15 6 v2 M4 13 h16" fill="none" stroke="currentColor" stroke-width="2"/></svg>`;
const SHOWROOM_SVG = `<svg viewBox="0 0 24 24"><path d="M4 9 L5 4 h14 l1 5 M5 9 v10 h14 V9 M9 19 v-6 h6 v6" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M3 9 h18" stroke="currentColor" stroke-width="2"/></svg>`;
const DEPOT_SVG = `<svg viewBox="0 0 24 24"><path d="M3 9 L12 4 L21 9 V20 H3 Z M7 20 v-7 h10 v7 M7 16 h10" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>`;

export function categoryIcon(p: { kind: string; category?: string }): string {
  if (p.kind === 'shared-location') return LIVE_SVG;
  switch (p.category) {
    case 'home':
      return HOME_SVG;
    case 'work':
      return WORK_SVG;
    case 'showroom':
      return SHOWROOM_SVG;
    case 'depot':
      return DEPOT_SVG;
    default:
      return STAR_SVG;
  }
}

/** Order for quick chips: named categories first, ad-hoc saves after. */
const CATEGORY_ORDER: Record<string, number> = { home: 0, work: 1, showroom: 2, depot: 3, other: 4 };

/** Simple fuzzy score: substring beats prefix beats spread subsequence. */
export function fuzzyScore(query: string, text: string): number {
  const q = query.toLowerCase().trim();
  const s = text.toLowerCase();
  if (!q) return 0;
  if (s.startsWith(q)) return 100;
  if (s.includes(q)) return 80;
  let qi = 0;
  let gaps = 0;
  let started = false;
  for (const ch of s) {
    if (qi < q.length && ch === q[qi]) {
      qi++;
      started = true;
    } else if (started && qi < q.length) {
      gaps++;
    }
  }
  return qi === q.length ? Math.max(10, 60 - gaps * 2) : 0;
}
const CLOCK_SVG = `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 7v5l3.5 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
